import React, { useState, useMemo, useEffect } from "react";
import queries from "../helpers/queries";
import { FiEdit, FiFileText, FiMonitor, FiX } from "react-icons/fi";
import { useForm } from "react-hook-form";
import { withUser } from "../Contexts/user";
import Select from "react-select";
import Modal from "../Components/modal";
import Table from "../Components/darkBasicTable";
import moment from "moment";
import { useQuery, useMutation } from "@apollo/react-hooks";
import useDimensions from "../Hooks/useDimensions";

const sevenDaysLater = moment().add(7, "days");

const formatFastCommStatus = (fastComm, labels) => {
    const n = Number(fastComm);
    if (n === 100) return labels.full;
    if (n === 50) return labels.half;
    if (n === 0) return labels.none;
    return "-";
};

/** Sponsor overriding comm takes precedence; leader comm only if user is not in sponsor list. */
const getOverridingCommEntryForUser = (caseRow, userId) => {
    const sponsorCommissions = caseRow?.sponsorCommissions;
    if (Array.isArray(sponsorCommissions)) {
        for (const item of sponsorCommissions) {
            if (item?.user?._id === userId) {
                return { item, source: "sponsor" };
            }
        }
    }

    const leaderCommissions = caseRow?.leaderCommissions;
    if (Array.isArray(leaderCommissions)) {
        for (const item of leaderCommissions) {
            if (item?.user?._id === userId) {
                return { item, source: "leader" };
            }
        }
    }

    return null;
};

const getOverridingCommStatusForUser = (
    caseRow,
    userId,
    labels = {
        full: "FastComm-Full (100%)",
        half: "FastComm-Half (50%)",
        none: "None (0%)",
    }
) => {
    const entry = getOverridingCommEntryForUser(caseRow, userId);
    if (!entry) return "";

    const fastComm =
        entry.source === "sponsor"
            ? entry.item.referralCommStatus?.fastComm
            : entry.item.leaderReferralCommStatus?.fastComm;

    return formatFastCommStatus(fastComm, labels);
};

const formFields = [
    {
        name: "spaPrice",
        label: "SPA Price",
        type: "numbers",
    },
    {
        name: "rebate",
        label: "Rebate",
        type: "numbers",
    },
    {
        name: "fees",
        label: "Fees",
        type: "numbers",
    },
    {
        name: "cashback",
        label: "Cash Back",
        type: "numbers",
    },
    {
        name: "netPrice",
        label: "Net Price",
        type: "numbers",
    },
    {
        name: "status",
        label: "Status",
    },
    {
        name: "bookingDate",
        label: "Booking Date",
        type: "date",
    },
    {
        name: "unitNumber",
        label: "Unit Number",
    },
    {
        name: "purchaserName",
        label: "Purchaser Name",
    },
    {
        name: "purchaserName2",
        label: "Purchaser Name 2",
    },
    {
        name: "purchaserName3",
        label: "Purchaser Name 3",
    },
    {
        name: "purchaserName4",
        label: "Purchaser Name 4",
    },
    {
        name: "purchaserName5",
        label: "Purchaser Name 5",
    },
];

const commissionFields = [
    {
        name: "agentCommission",
        label: "Agent Commission",
        type: "numbers",
    },
    {
        name: "commissionPercentage",
        label: "Commission Percentage",
        type: "numbers",
    },
    {
        name: "leaderCommission",
        label: "Leader Commission",
        type: "numbers",
    },
    {
        name: "leadercommissionPercentage",
        label: "Leader Commission Percentage",
        type: "numbers",
    },
    {
        name: "primeCommission",
        label: "Prime Leader Commission",
        type: "numbers",
    },
    {
        name: "primeleadercommissionPercentage",
        label: "Prime Commission Percentage",
        type: "numbers",
    },
];

const statusOptions = [
    {
        label: "All",
        value: "all",
    },
    {
        label: "Pending Loan",
        value: "pending",
    },
    {
        label: "Cancelled",
        value: "cancel",
    },
    {
        label: "Converted",
        value: "converted",
    },
    {
        label: "SignedLo",
        value: "signedLo",
    },
    {
        label: "Top30 (Converted + SignedLo)",
        value: "top30",
    }
];

const Cases = ({ user, location }) => {
    const [modalData, setModalData] = useState({});
    const [agentFastCommList, setAgentFastCommList] = useState([]);
    const [modalState, setModalState] = useState();
    const [changeCommissionStatusModal, setChangeCommissionStatusModal] =
        useState(false);
    const [
        changeAgentCommissionStatusModal,
        setChangeAgentCommissionStatusModal,
    ] = useState(false);
    const [typeCommissionStatus, setTypeCommissionStatus] = useState({});
    const [typeReferralCommissionStatus, setTypeReferralCommissionStatus] =
        useState(undefined);

    const [commissionState, setCommissionState] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [showDetails, setShowDetails] = useState(false);

    const size = useDimensions();

    const resetModalData = () => {
        setModalState(false);
    };

    const resetCommissionState = () => {
        setCommissionState(false);
    };
    const resetCommissionStatusModal = () => {
        setChangeCommissionStatusModal(false);
        resetStatusCommissionForm();
    };

    const resetAgentCommissionStatusModal = () => {
        setChangeAgentCommissionStatusModal(false);
        resetStatusCommissionForm();
    };

    const {
        register: registerStatusForm,
        handleSubmit: handleSubmitStatusForm,
        getValues: getValuesStatusCommissionForm,
        reset: resetStatusCommissionForm,
    } = useForm({
        defaultValues: {
            fastComm: undefined,
        },
    });

    const [editCommissionStatus] = useMutation(queries.EDIT_COMMISSION_STATUS);

    const handleEditStatus = async () => {
        try {
            const formValues = getValuesStatusCommissionForm();

            const result = await editCommissionStatus({
                variables: {
                    memberId: modalData ? modalData.memberId : undefined,
                    caseId: modalData
                        ? modalData.nested
                            ? modalData.nested._id
                            : undefined
                        : undefined,
                    fastComm: formValues.fastComm
                        ? parseFloat(formValues.fastComm)
                        : undefined,
                    type: typeCommissionStatus,
                },
            });

            if (result.data) {
                alert("Success");
                setModalData((prev) => ({
                    ...prev,
                    members: result.data.editCommissionStatus.members,
                    sponsorCommissions:
                        result.data.editCommissionStatus.sponsorCommissions,
                    leaderCommissions:
                        result.data.editCommissionStatus.leaderCommissions,
                    ptp1Commissions:
                        result.data.editCommissionStatus.ptp1Commissions,
                    ptp2Commissions:
                        result.data.editCommissionStatus.ptp2Commissions,
                    ptpCommissions:
                        result.data.editCommissionStatus.ptpCommissions,
                }));

                setChangeAgentCommissionStatusModal(false);
            }
        } catch (err) {
            alert("Failed");
            console.log(err);
        }
    };


    const handleEditLeaderStatus = async () => {
        try {
            const formValues = getValuesStatusCommissionForm();

            const result = await editCommissionStatus({
                variables: {
                    memberId: modalData ? modalData.memberId : undefined,
                    caseId: modalData
                        ? modalData.nested
                            ? modalData.nested._id
                            : undefined
                        : undefined,
                    fastComm: formValues.fastComm
                        ? parseFloat(formValues.fastComm)
                        : undefined,
                    type: typeReferralCommissionStatus,
                },
            });

            if (result.data) {
                alert("Success");
                setModalData((prev) => ({
                    ...prev,
                    members: result.data.editCommissionStatus.members,
                    sponsorCommissions:
                        result.data.editCommissionStatus.sponsorCommissions,
                    leaderCommissions:
                        result.data.editCommissionStatus.leaderCommissions,
                    ptp1Commissions:
                        result.data.editCommissionStatus.ptp1Commissions,
                    ptp2Commissions:
                        result.data.editCommissionStatus.ptp2Commissions,
                    ptpCommissions:
                        result.data.editCommissionStatus.ptpCommissions,
                }));

                setChangeCommissionStatusModal(false);
            }
        } catch (err) {
            alert("Failed");
            console.log(err);
        }
    };

    /** Single border on control only — avoid `cases-filter-select` on Select (that class borders the outer container and doubles the border). */
    const casesDetailsFilterSelectStyles = useMemo(
        () => ({
            container: (base) => ({
                ...base,
                border: "none",
                padding: 0,
                background: "transparent",
            }),
            control: (base, state) => ({
                ...base,
                minWidth: "180px",
                borderRadius: "8px",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                borderColor: "rgba(0, 0, 0, 0.1)",
                backgroundColor: "#fff",
                boxShadow: "none",
                outline: "none",
                ...(state.isFocused && {
                    borderColor: "rgba(0, 0, 0, 0.1)",
                    boxShadow: "none",
                }),
            }),
            valueContainer: (base) => ({
                ...base,
                padding: "0.35rem 0.5rem",
            }),
            indicatorsContainer: (base) => ({
                ...base,
                paddingRight: "0.35rem",
            }),
        }),
        []
    );

    const [paginationState, setPaginationState] = useState({
        limit: 10,
        page: 1,
    });

    const [filters, setFilters] = useState({
        project: location.state
            ? location.state.project
                ? location.state.project
                : null
            : null,
        dateTo: moment(Date.now()).format("YYYY-MM-DD"),
        dateFrom: moment(Date.now()).subtract(1, "months").format("YYYY-MM-DD"),
        status: {
            label: "All",
            value: "all",
        },
        downline: null,
    });

    // Separate state for date inputs to prevent immediate updates
    const [dateInputs, setDateInputs] = useState({
        dateFrom: moment(Date.now()).subtract(1, "months").format("YYYY-MM-DD"),
        dateTo: moment(Date.now()).format("YYYY-MM-DD"),
    });

    // Debounced effect to update filters after user stops typing
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (dateInputs.dateFrom !== filters.dateFrom || dateInputs.dateTo !== filters.dateTo) {
                setIsLoading(true);
                setFilters((prev) => ({
                    ...prev,
                    dateFrom: dateInputs.dateFrom,
                    dateTo: dateInputs.dateTo,
                }));
            }
        }, 1500);

        return () => clearTimeout(timeout);
    }, [dateInputs.dateFrom, dateInputs.dateTo, filters.dateFrom, filters.dateTo]);

    const { data: projectsData } = useQuery(queries.FETCH_PROJECTS, {
        fetchPolicy: "network-only",
        variables: {
            limit: 9999,
            page: 1,
        },
        onError: ({ graphQLErrors, networkError }) => {
            if (graphQLErrors) {
                for (const graphQLError of graphQLErrors) {
                    console.log(graphQLError);
                }
            }

            if (networkError) {
                console.log(networkError);
            }
        },
    });

    const { data: casesData, loading: casesLoading } = useQuery(
        queries.FETCH_CASES_REPORT,
        {
            fetchPolicy: "network-only",
            variables: {
                limit: paginationState.limit,
                page: paginationState.page,
                by: paginationState.by ? paginationState.by : undefined,
                dir: paginationState.dir ? paginationState.dir : undefined,
                project: filters.project ? filters.project.value : undefined,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                status: filters.status ? filters.status.value : undefined,
                downline: filters.downline ? filters.downline.id : undefined,
            },
            onCompleted: ({ fetchCasesAgent: { pagination } }) => {
                setIsLoading(false);
                setPaginationState(pagination);
                setAgentFastCommList(
                    casesData.fetchCasesAgent.data.filter(row => {
                        let commStatus = "";
                        row.members.forEach((item) => {
                            if (item.member._id === user.auth.userId) {
                                commStatus =
                                    item.fullClaimComm != null
                                        ? "Full Claim"
                                        : item.secondBatchComm != null
                                        ? "Second Batch"
                                        : item.firstBatchComm != null
                                        ? "First Batch"
                                        : item.memberCommStatus && item.memberCommStatus.fastComm === 100
                                        ? "FastComm-Full (100%)"
                                        : item.memberCommStatus && item.memberCommStatus.fastComm === 50
                                        ? "FastComm-Half (50%)"
                                        : item.memberCommStatus && item.memberCommStatus.fastComm === 0
                                        ? "None (0%)"
                                        : "-";
                                return;
                            }
                        });

                        return (
                            
                            (
                                (commStatus === "-" && row.status === "pending" &&
                                moment(row.bookingDate).isAfter("2025-03-16") &&
                                moment(row.createdAt).isBefore(sevenDaysLater))
                                ||
                                ((commStatus === "-" || commStatus === "FastComm-Full" || commStatus === "FastComm-Half" || commStatus === "None") && ( row.status === "pending" || row.status === "converted") &&
                                moment(row.bookingDate).isAfter("2025-03-09") &&
                                moment(row.bookingDate).isBefore("2025-03-17"))
                            )
                        );
                    })
                );
            },
        }
    );

    const columns = useMemo(() => {
        const NumberFormatter = new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        if (size.width > 650) {
            return [
                {
                    Header: "Project Name",
                    accessor: "project",
                    Cell: ({ value }) => {
                        if (value) {
                            return <div> {value.name}</div>;
                        }

                        return <div></div>;
                    },
                },
                {
                    Header: "Unit No.",
                    accessor: "unitNumber",
                },
              


                {
                    Header: "Net Price",
                    accessor: "netPrice",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Agent Comm",
                    id: "commStatus",
                    Cell: ({ row: { original } }) => {
                        let commStatus = "";
                        let memberId = undefined;
                        if (original.members && Array.isArray(original.members)) {
                            original.members.forEach((item) => {
                                if (item && item.member && item.member._id === user.auth.userId) {
                                    if (item.fullClaimComm != null) {
                                        commStatus = "Full Claim";
                                    } else if (item.secondBatchComm != null) {
                                        commStatus = "Second Batch";
                                    } else if (item.firstBatchComm != null) {
                                        commStatus = "First Batch";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 100) {
                                        commStatus = "FastComm-Full (100%)";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 50) {
                                        commStatus = "FastComm-Half (50%)";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 0) {
                                        commStatus = "None (0%)";
                                    } else {
                                        commStatus = "-";
                                    }
                                    memberId = item._id;
                                    setTypeCommissionStatus("member");
                                    return;
                                }
                            });
                        } else {
                            commStatus = "-";
                        }

                        return (
                            <div className="flex justify-center items-center">
                                <div>{commStatus}</div>
                            </div>
                        );
                    },
                },

                {
                    Header: "Overriding Comm",
                    id: "referralcommStatus",
                    Cell: ({ row: { original } }) => {
                        let commStatus = "-";
                        const userId = user.auth.userId;
                        let entry = null;

                        // Try leaderCommissions first
                        if (Array.isArray(original.leaderCommissions)) {
                            entry = original.leaderCommissions.find(
                                (item) => item?.user?._id === userId
                            );
                        }

                        // If not found in leaderCommissions, check sponsorCommissions
                        if (!entry && Array.isArray(original.sponsorCommissions)) {
                            entry = original.sponsorCommissions.find(
                                (item) => item?.user?._id === userId
                            );
                        }

                        if (entry) {
                            // Try to display FastComm status if possible
                            let fastComm = entry.leaderReferralCommStatus?.fastComm ?? entry.referralCommStatus?.fastComm;
                            if (fastComm === 100) {
                                commStatus = "FastComm-Full (100%)";
                            } else if (fastComm === 50) {
                                commStatus = "FastComm-Half (50%)";
                            } else if (fastComm === 0) {
                                commStatus = "None (0%)";
                            }
                        }
              

                        return (
                            <div className="flex justify-center items-center">
                                <div>{commStatus}</div>
                            </div>
                        );
                    },
                },
                {
                    Header: "Status",
                    accessor: "status",
                    Cell: ({ value }) => (
                        <div className="capitalize">{value}</div>
                    ),
                },
                {
                    Header: "Booking Date",
                    accessor: "bookingDate",
                },
                {
                    Header: "Converted Date",
                    accessor: "convertDate",
                    Cell: ({ value }) => (
                        <div className="capitalize">
                            {value ? moment(value).format("YYYY-MM-DD") : "-"}
                        </div>
                    ),
                },
                {
                    Header: "Involved Agent",
                    accessor: "worker",
                    Cell: ({ value }) => (
                        <div className="capitalize">{value}</div>
                    ),
                },

                {
                    Header: "",
                    id: "actions",
                    Cell: ({ row: { original } }) => {
                        return (
                            <div className="flex justify-center items-center cursor-pointer">
                                <div
                                    className="mr-2"
                                    onClick={() => {
                                        setModalState(true);
                                        setModalData({
                                            ...original,
                                        });
                                    }}
                                >
                                    <FiFileText />
                                </div>

                                <div
                                    onClick={() => {
                                        setCommissionState(true);
                                        let totalAgentComm = 0;
                                        let totalLeaderComm = 0;
                                        let leadercommissionPercentage = 0;
                                        let totalSponsorComm = 0;
                                        let primeleadercommissionPercentage = 0;
                                        let commissionPercentage = 0;

                                        if (original.members && Array.isArray(original.members)) {
                                            original.members.forEach((item) => {
                                                if (
                                                    item && item.member &&
                                                    item.member._id ===
                                                    user.auth.userId
                                                ) {
                                                    const agentComm =
                                                        item.newCommission
                                                            ? (item.newCommission *
                                                                  original.netPrice) /
                                                              100 /
                                                              1.08
                                                            : (item.commission *
                                                                  original.netPrice) /
                                                              100 /
                                                              1.08;
                                                    totalAgentComm =
                                                        totalAgentComm + (item.incentive || agentComm);
                                                    const commission =
                                                        item.newCommission
                                                            ? item.newCommission
                                                            : item.commission;
                                                    commissionPercentage =
                                                        commissionPercentage +
                                                        commission;
                                                    return;
                                                }
                                            });
                                        }

                                        if (original.leaderCommissions && Array.isArray(original.leaderCommissions)) {
                                            original.leaderCommissions.forEach(
                                                (item, i) => {
                                                    if (
                                                        item && item.user &&
                                                        item.user._id ===
                                                        user.auth.userId
                                                    ) {
                                                        const leaderComm =
                                                            ((item.newCommission ? item.newCommission : item.commission) *
                                                                original.netPrice) /
                                                            100 /
                                                            1.08;
                                                        totalLeaderComm =
                                                            totalLeaderComm +
                                                            (item.incentive && item.incentive !== 0 ? (item.newAmount ? item.newAmount : item.amount) : leaderComm);
                                                        primeleadercommissionPercentage =
                                                            item.newCommission
                                                                ? item.newCommission
                                                                : item.commission;
                                                    }
                                                }
                                            );
                                        }

                                        if (original.sponsorCommissions && Array.isArray(original.sponsorCommissions)) {
                                            original.sponsorCommissions.forEach(
                                                (item, i) => {
                                                    if (
                                                        item && item.user &&
                                                        item.user._id ===
                                                        user.auth.userId
                                                    ) {
                                                        const sponsorComm =
                                                            ((item.newCommission ? item.newCommission : item.commission) *
                                                                original.netPrice) /
                                                            100 /
                                                            1.08;
                                                        totalSponsorComm =
                                                            totalSponsorComm +
                                                            (item.incentive && item.incentive !== 0 ? (item.newAmount ? item.newAmount : item.amount) : sponsorComm);
                                                        leadercommissionPercentage =
                                                            item.newCommission
                                                                ? item.newCommission
                                                                : item.commission;
                                                    }
                                                }
                                            );
                                        }
                                        setModalData({
                                            ...original,
                                            agentCommission: totalAgentComm,
                                            leaderCommission: totalSponsorComm,
                                            primeCommission: totalLeaderComm,
                                            commissionPercentage:
                                                commissionPercentage,
                                            leadercommissionPercentage:
                                                leadercommissionPercentage,
                                            primeleadercommissionPercentage:
                                                primeleadercommissionPercentage,
                                        });
                                    }}
                                >
                                    <FiMonitor />
                                </div>
                            </div>
                        );
                    },
                },
            ];
        } else {
            return [
               
                {
                    Header: "Unit Number",
                    id: "unitNumber",
                    Cell: ({ row: { original } }) => {
                        if (original) {
                            return (
                                <div className="capitalize">
                                    {" "}
                                    {original.unitNumber}
                                </div>
                            );
                        }

                        return <div></div>;
                    },
                },
                {
                    Header: "Booking Date",
                    accessor: "bookingDate",
                },
                {
                    Header: "Agent Commission",
                    id: "commission",
                    Cell: ({ row: { original } }) => {
                        let totalAgentComm = 0;
                        if (original.members && Array.isArray(original.members)) {
                            original.members.forEach((item) => {
                                if (item && item.member && item.member._id === user.auth.userId) {
                                    const agentComm = item.newCommission
                                        ? (item.newCommission * original.netPrice) /
                                          100 /
                                          1.08
                                        : (item.commission *
                                              original.netPrice) /
                                          100 /
                                          1.08;
                                    totalAgentComm = totalAgentComm + (item.incentive || agentComm);
                                    return;
                                }
                            });
                        }
                        return (
                            <div className="flex justify-center items-center">
                                RM {NumberFormatter.format(totalAgentComm)}
                            </div>
                        );
                    },
                },
                {
                    Header: "Agent Status",
                    id: "commStatus",
                    Cell: ({ row: { original } }) => {
                        let commStatus = "";
                        let memberId = undefined;
                        if (original.members && Array.isArray(original.members)) {
                            original.members.forEach((item) => {
                                if (item && item.member && item.member._id === user.auth.userId) {
                                    if (item.fullClaimComm != null) {
                                        commStatus = "Full Claim";
                                    } else if (item.secondBatchComm != null) {
                                        commStatus = "Second Batch";
                                    } else if (item.firstBatchComm != null) {
                                        commStatus = "First Batch";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 100) {
                                        commStatus = "FastComm-Full";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 50) {
                                        commStatus = "FastComm-Half";
                                    } else if (item.memberCommStatus && item.memberCommStatus.fastComm === 0) {
                                        commStatus = "None";
                                    } else {
                                        commStatus = "-";
                                    }
                                    memberId = item._id;
                                    setTypeCommissionStatus("member");
                                    return;
                                }
                            });
                        } else {
                            commStatus = "-";
                        }

                        return (
                            <div className="flex justify-center items-center">
                                <div>{commStatus}</div>
                                {
                                    ((commStatus === "-" && original.status === "pending" &&
                                        moment(original.bookingDate).isAfter(
                                            "2025-03-16"
                                        ) &&
                                        moment(original.createdAt).isBefore(
                                            sevenDaysLater
                                        ))) && (
                                        <div className="cursor-pointer ml-2">
                                            <FiEdit
                                                onClick={() => {
                                                    setTypeReferralCommissionStatus(
                                                        undefined
                                                    );

                                                    if (original.members && Array.isArray(original.members)) {
                                                        original.members.forEach(
                                                            (item) => {
                                                                if (
                                                                    item && item.member &&
                                                                    item.member._id ===
                                                                    user.auth.userId
                                                                ) {
                                                                    setTypeCommissionStatus(
                                                                        "member"
                                                                    );
                                                                    return;
                                                                }
                                                            }
                                                        );
                                                    }

                                                    setModalData({
                                                        ...modalData,
                                                        nested: original,
                                                        memberId: memberId,
                                                    });

                                                    setChangeAgentCommissionStatusModal(
                                                        true
                                                    );
                                                }}
                                            ></FiEdit>
                                        </div>
                                    )}
                            </div>
                        );
                    },
                },

                {
                    Header: "Overriding Status",
                    id: "referralcommStatus",
                    Cell: ({ row: { original } }) => {
                        const entry = getOverridingCommEntryForUser(
                            original,
                            user.auth.userId
                        );
                        const commStatus =
                            getOverridingCommStatusForUser(
                                original,
                                user.auth.userId,
                                {
                                    full: "FastComm-Full",
                                    half: "FastComm-Half",
                                    none: "None",
                                }
                            ) || "-";
                        const memberId = entry?.item?._id;

                        return (
                            <div className="flex justify-center items-center">
                                <div>{commStatus}</div>
                                {
                                    ((commStatus === "-" && moment(original.bookingDate).isAfter(
                                        "2025-03-16"
                                    ) &&
                                        moment(original.createdAt).isBefore(
                                            sevenDaysLater
                                        ) &&
                                        (original.status === "converted" ||
                                            original.status === "pending"))) && (
                                        <div className="cursor-pointer ml-2">
                                            <FiEdit
                                                onClick={() => {
                                                    if (original.sponsorCommissions && Array.isArray(original.sponsorCommissions)) {
                                                        original.sponsorCommissions.forEach(
                                                            (item) => {
                                                                if (
                                                                    item && item.user &&
                                                                    item.user._id ===
                                                                    user.auth.userId
                                                                ) {
                                                                    setTypeReferralCommissionStatus(
                                                                        "referral"
                                                                    );
                                                                    return;
                                                                }
                                                            }
                                                        );
                                                    }

                                                    const isUserInSponsorCommissions =
                                                        original.sponsorCommissions && Array.isArray(original.sponsorCommissions) &&
                                                        original.sponsorCommissions.some(
                                                            (item) =>
                                                                item && item.user &&
                                                                item.user._id ===
                                                                user.auth.userId
                                                        );

                                                    if (original.leaderCommissions && Array.isArray(original.leaderCommissions)) {
                                                        original.leaderCommissions.forEach(
                                                            (item) => {
                                                                if (
                                                                    item && item.user &&
                                                                    item.user._id ===
                                                                    user.auth.userId
                                                                ) {
                                                                    setTypeReferralCommissionStatus(
                                                                        isUserInSponsorCommissions
                                                                            ? "memberreferralleader"
                                                                            : "leaderReferral"
                                                                    );
                                                                    return;
                                                                }
                                                            }
                                                        );
                                                    }

                                                    setModalData({
                                                        ...modalData,
                                                        nested: original,
                                                        memberId: memberId,
                                                    });

                                                    setChangeCommissionStatusModal(
                                                        true
                                                    );
                                                }}
                                            ></FiEdit>
                                        </div>
                                    )}
                            </div>
                        );
                    },
                },
            ];
        }
    }, [modalData, size.width, setModalData, user.auth.userId,agentFastCommList]);

    const NumberFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return (
        <>
            {isLoading ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                </div>
            ) : (
                <div className="my-10 lg:my-0 lg:p-8">
                    {!showDetails ? (
                        // Summary Card
                        <div className="cases-summary-card">
                            <h1 className="cases-title">CASES</h1>
                            <div className="cases-grid">
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Total Cases</div>
                                    <div className="cases-stat-value">
                                        {casesData?.fetchCasesAgent?.totals?.cases || "0"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Total SPA Price</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchCasesAgent?.totals?.spaPrice ? NumberFormatter.format(casesData.fetchCasesAgent.totals.spaPrice) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Total Net Price</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchCasesAgent?.totals?.netPrice ? NumberFormatter.format(casesData.fetchCasesAgent.totals.netPrice) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Total Net GDV</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchCasesAgent?.totals?.totalNetGdv ? NumberFormatter.format(casesData.fetchCasesAgent.totals.totalNetGdv) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Total Group Net GDV</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchCasesAgent?.totals?.totalGroupNetGdv ? NumberFormatter.format(casesData.fetchCasesAgent.totals.totalGroupNetGdv) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Commission Percentage</div>
                                    <div className="cases-stat-value">
                                        {casesData?.fetchCasesAgent?.totals?.totalCommissionPercentage || "0"}% ({casesData?.fetchCasesAgent?.totals?.totalLegacyCommissionPercentage || "0"}%)
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Personal Commission</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchCasesAgent?.totals?.totalCommission ? NumberFormatter.format(casesData.fetchCasesAgent.totals.totalCommission) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Leader Commission</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchCasesAgent?.totals?.totalLeaderComm ? NumberFormatter.format(casesData.fetchCasesAgent.totals.totalLeaderComm) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">Prime Leader Commission</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchCasesAgent?.totals?.totalPrimeLeaderComm ? NumberFormatter.format(casesData.fetchCasesAgent.totals.totalPrimeLeaderComm) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">PTP 5 Commission</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchCasesAgent?.totals?.totalPtpReferralComm ? NumberFormatter.format(casesData.fetchCasesAgent.totals.totalPtpReferralComm) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">PTP 2 Commission</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchCasesAgent?.totals?.totalPtp2ReferralComm ? NumberFormatter.format(casesData.fetchCasesAgent.totals.totalPtp2ReferralComm) : "0.00"}
                                    </div>
                                </div>
                                <div className="cases-stat-box">
                                    <div className="cases-stat-label">PTP 1 Commission</div>
                                    <div className="cases-stat-value">
                                        RM{casesData?.fetchCasesAgent?.totals?.totalPtp1ReferralComm ? NumberFormatter.format(casesData.fetchCasesAgent.totals.totalPtp1ReferralComm) : "0.00"}
                                    </div>
                                </div>
                            </div>
                            <button 
                                className="cases-view-details"
                                onClick={() => setShowDetails(true)}
                            >
                                VIEW DETAILS
                            </button>
                        </div>
                    ) : (
                        // Details Card
                        <div className="cases-details-card">
                            <div className="cases-details-header">
                                <h2 className="cases-details-title">CASES DETAILS</h2>
                                <button 
                                    className="cases-back-btn"
                                    onClick={() => setShowDetails(false)}
                                >
                                    Back to Summary
                                </button>
                            </div>
                            
                            <div className="cases-filters">
                                        <input
                                            type="date"
                                    className="cases-filter-select"
                                    value={dateInputs.dateFrom}
                                            onChange={(e) => {
                                                setDateInputs((prev) => ({
                                                    ...prev,
                                                    dateFrom: e.target.value,
                                                }));
                                            }}
                                        />
                                        <input
                                            type="date"
                                    className="cases-filter-select"
                                    value={dateInputs.dateTo}
                                            onChange={(e) => {
                                                setDateInputs((prev) => ({
                                                    ...prev,
                                                    dateTo: e.target.value,
                                                }));
                                            }}
                                        />
                                        <Select
                                            styles={casesDetailsFilterSelectStyles}
                                            options={statusOptions}
                                            value={filters.status}
                                            placeholder="Status"
                                            onChange={(val) => {
                                                setPaginationState((prev) => ({ ...prev, page: 1 }));
                                                setIsLoading(true);
                                                setFilters((prev) => ({ ...prev, status: val }));
                                            }}
                                        />
                                        <Select
                                            styles={casesDetailsFilterSelectStyles}
                                            options={
                                                projectsData
                                                    ? projectsData.fetchProjectsAgent.data.map((project) => ({
                                                          label: project.name,
                                                          value: project._id,
                                                      }))
                                                    : []
                                            }
                                            value={filters.project}
                                            placeholder="Project"
                                            onChange={(val) => {
                                                setPaginationState((prev) => ({ ...prev, page: 1 }));
                                                setIsLoading(true);
                                                setFilters((prev) => ({ ...prev, project: val }));
                                            }}
                                        />
                                <button className="cases-search-btn">Search</button>
                                    </div>

                                    <div className="cases-table-actions">
                            <button
                                    className="cases-action-btn"
                                onClick={() => {
                                       window.location.href = "/fastcases";
                                }}
                            >
                                Fast / Full Comm
                            </button>
                        </div>
                            <div className="cases-table-container">
                        <Table
                            columns={columns}
                                    data={casesData ? casesData.fetchCasesAgent.data : []}
                            updatePagination={setPaginationState}
                            paginationState={paginationState}
                            loading={casesLoading}
                                    pageCount={casesData ? casesData.fetchCasesAgent.pagination.totalPages : 0}
                                    totalCount={casesData ? casesData.fetchCasesAgent.pagination.documents : 0}
                            hiddenFields={
                                user.auth.ranking.name === "agent"
                                    ? ["leaderCommission", "primeCommission"]
                                    : user.auth.ranking.name === "leader"
                                    ? ["primeCommission"]
                                    : undefined
                            }
                        />
                            </div>
                        </div>
                    )}
                </div>
            )}
            {/* Keep existing modals */}
                        {modalState && (
                            <Modal reset={resetModalData}>
                                <div className="container h-full mx-auto flex justify-center items-center">
                                    <div className="commission-modal-container" style={{ maxHeight: '90vh', width: '800px', maxWidth: '95vw' }}>
                                        <div className="commission-modal-header">
                                            <h2 className="commission-modal-title">
                                                Case Summary
                                            </h2>
                                        <div className="flex justify-end">
                                            <FiX
                                                onClick={() => {
                                                    setModalState(false);
                                                }}
                                                className="cursor-pointer"
                                            />
                                        </div>
                                        </div>
                                        <div className="commission-modal-body" style={{ maxHeight: 'calc(90vh - 80px)', overflowY: 'auto', paddingRight: '10px' }}>
                                        {formFields.map((item, i) => {
                                            if (
                                                item.name === "purchaserName" ||
                                                item.name === "purchaserIc" ||
                                                item.name === "purchaserContact"
                                            ) {
                                                return (
                                                    <div
                                                        className="flex row"
                                                        key={i}
                                                            style={{ marginBottom: '12px' }}
                                                    >
                                                            <div className="lg:w-1/3 w-1/2 text-right flex items-center justify-start mb-2 text-sm text-lynch font-medium whitespace-nowrap pr-4">
                                                            {item.label} :
                                                        </div>
                                                        <div className="my-2 p-3 lg:w-2/3 w-1/2 rounded-lg block bg-cool-gray border font-semibold text-lynch text-xs capitalize">
                                                            {modalData.purchaser
                                                                ? modalData
                                                                      .purchaser[
                                                                      item.name
                                                                  ]
                                                                : ""}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div
                                                    className="flex row"
                                                    key={item.name}
                                                        style={{ marginBottom: '12px' }}
                                                >
                                                        <div className="lg:w-1/3 w-1/2 text-right flex items-center justify-start mb-2 text-sm text-lynch font-medium whitespace-nowrap pr-4">
                                                        {item.label} :
                                                    </div>
                                                        <div className="my-2 p-3 lg:w-2/3 w-1/2 rounded-lg block bg-cool-gray border font-semibold text-lynch text-xs capitalize">
                                                        {item.type === "numbers"
                                                            ? `RM ${NumberFormatter.format(
                                                                  modalData[
                                                                      item.name
                                                                  ]
                                                              )}`
                                                            : modalData[
                                                                  item.name
                                                              ]}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                            <div className="flex row" style={{ marginBottom: '12px' }}>
                                                <div className="lg:w-1/3 w-1/2 text-right flex items-center justify-start mb-2 text-sm text-lynch font-medium whitespace-nowrap pr-4">
                                                Project :
                                            </div>
                                                <div className="my-2 p-3 lg:w-2/3 w-1/2 rounded-lg block bg-cool-gray border font-semibold text-lynch text-xs">
                                                {modalData.project.name}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Modal>
                        )}
                        {changeCommissionStatusModal && (
                            <Modal reset={resetCommissionStatusModal}>
                                <div className="container h-full mx-auto flex justify-center items-center">
                                    <div className="commission-modal-container">
                                        <div className="commission-modal-header">
                                            <h2 className="commission-modal-title">
                                            Change Commission Status
                                            </h2>
                                        </div>
                                        <div className="commission-modal-body">
                                        <form
                                            onSubmit={handleSubmitStatusForm(
                                                typeReferralCommissionStatus
                                                    ? handleEditLeaderStatus
                                                    : handleEditStatus
                                            )}
                                        >
                                            <div>
                                                    <div className="commission-modal-label">
                                                        Fast Commission Status
                                                </div>
                                                <select
                                                        className="commission-modal-select"
                                                    name="fastComm"
                                                    ref={registerStatusForm({
                                                        required: false,
                                                    })}
                                                    placeholder="Status"
                                                    type="string"
                                                >
                                                    <option value="0">
                                                        None
                                                    </option>
                                                    <option value="100">
                                                        Full Comm
                                                    </option>
                                                </select>
                                            </div>
                                                <button className="commission-modal-button">
                                                Submit
                                            </button>
                                        </form>
                                        </div>
                                    </div>
                                </div>
                            </Modal>
                        )}


                        {changeAgentCommissionStatusModal && (
                            <Modal reset={resetAgentCommissionStatusModal}>
                                <div className="container h-full mx-auto flex justify-center items-center">
                                    <div className="commission-modal-container">
                                        <div className="commission-modal-header">
                                            <h2 className="commission-modal-title">
                                            Change Commission Status
                                            </h2>
                                        </div>
                                        <div className="commission-modal-body">
                                        <form
                                            onSubmit={handleSubmitStatusForm(
                                                typeReferralCommissionStatus
                                                    ? handleEditLeaderStatus
                                                    : handleEditStatus
                                            )}
                                        >
                                            <div>
                                                    <div className="commission-modal-label">
                                                        Fast Commission Status
                                                </div>
                                                <select
                                                        className="commission-modal-select"
                                                    name="fastComm"
                                                    ref={registerStatusForm({
                                                        required: false,
                                                    })}
                                                    placeholder="Status"
                                                    type="string"
                                                >
                                                    <option value="50">
                                                        Fast Comm
                                                    </option>
                                                    <option value="100">
                                                        Full Comm
                                                    </option>
                                                </select>
                                            </div>
                                                <button className="commission-modal-button">
                                                Submit
                                            </button>
                                        </form>
                                        </div>
                                    </div>
                                </div>
                            </Modal>
                        )}

                        {commissionState && (
                            <Modal reset={resetCommissionState}>
                                <div className="container h-full mx-auto flex justify-center items-center">
                                    <div className="commission-modal-container" style={{ maxHeight: '90vh', width: '800px', maxWidth: '95vw' }}>
                                        <div className="commission-modal-header">
                                            <h2 className="commission-modal-title">
                                                Commission Details
                                            </h2>
                                        <div className="flex justify-end">
                                            <FiX
                                                onClick={() => {
                                                    setCommissionState(false);
                                                }}
                                                className="cursor-pointer"
                                            />
                                        </div>
                                        </div>
                                        <div className="commission-modal-body" style={{ maxHeight: 'calc(90vh - 80px)', overflowY: 'auto', paddingRight: '10px' }}>
                                        {commissionFields.map((item, i) => {
                                            if (
                                                item.name === "purchaserName" ||
                                                item.name === "purchaserIc" ||
                                                item.name === "purchaserContact"
                                            ) {
                                                return (
                                                    <div
                                                        className="flex row"
                                                        key={i}
                                                    >
                                                        <div className="lg:w-1/3 w-1/2 text-right flex items-center justify-start mb-2 text-sm text-lynch font-medium">
                                                            {item.label} :
                                                        </div>
                                                        <div className="my-2 p-3 lg:w-2/3 w-1/2 rounded-lg block bg-cool-gray border font-semibold text-lynch text-xs capitalize">
                                                            {modalData.purchaser
                                                                ? modalData
                                                                      .purchaser[
                                                                      item.name
                                                                  ]
                                                                : ""}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div
                                                    className="flex row"
                                                    key={item.name}
                                                        style={{ marginBottom: '12px' }}
                                                    >
                                                        <div className="lg:w-1/3 w-1/2 text-right flex items-center justify-start mb-2 text-sm text-lynch font-medium whitespace-nowrap pr-4">
                                                            {item.name === "leadercommissionPercentage" 
                                                                ? "Leader Commission %" 
                                                                : item.name === "primeleadercommissionPercentage"
                                                                ? "Prime Leader Commission %"
                                                                : item.label} :
                                                    </div>
                                                        <div className="my-2 p-3 lg:w-2/3 w-1/2 rounded-lg block bg-cool-gray border font-semibold text-lynch text-xs capitalize">
                                                        {item.type ===
                                                            "numbers" &&
                                                        item.label !==
                                                            "Commission Percentage" &&
                                                        item.label !==
                                                            "Leader Commission Percentage" &&
                                                        item.label !==
                                                            "Prime Commission Percentage"
                                                            ? `RM ${NumberFormatter.format(
                                                                  modalData[
                                                                      item.name
                                                                  ]
                                                              )}`
                                                            : `${
                                                                  modalData[
                                                                      item.name
                                                                  ]
                                                              } %`}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        </div>
                                    </div>
                                </div>
                            </Modal>
            )}
        </>
    );
};

export default withUser(Cases);